import { ChartType } from "@custom-types/analytics-types";
import { createSelector } from "@reduxjs/toolkit";
import { selectedProjectIdSelector } from "@store/projects/projects-selector";
import { canWriteImg360Selector } from "@store/sdb-company/sdb-company-selector";
import { getBaseStatPairs, getChartsData } from "@utils/analytics-utils";
/**
 * Returns the selected time frame.
 */
export const selectedAnalyticsTimeFrameSelector = createSelector(state => state.analytics.selectedAnalyticsTimeFrame, selectedTimeFrame => {
  return selectedTimeFrame;
});

/** Returns the company stats */
export const companyStatsSelector = createSelector(state => state.analytics.companyStats, companyStats => {
  return companyStats;
});

/**
 * Charts that only carry information for plans that include 360° capture. Point-cloud-only plans
 * are shown the remaining charts only.
 */
export const IMG360_ONLY_CHART_TYPES = [ChartType.projectSqft, ChartType.projectSpheres, ChartType.projectSheets];

/**
 * Returns the chart types the selected workspace is shown, in display order.
 * Point-cloud-only plans lose the charts in {@link IMG360_ONLY_CHART_TYPES}.
 */
export const visibleChartTypesSelector = createSelector(canWriteImg360Selector, canWriteImg360 => Object.values(ChartType).filter(chartType => canWriteImg360 || !IMG360_ONLY_CHART_TYPES.includes(chartType)));

/** Returns the charts data that will be rendered as charts*/
export const chartsDataSelector = createSelector(selectedAnalyticsTimeFrameSelector, companyStatsSelector, visibleChartTypesSelector, (timeFrame, companyStats, visibleChartTypes) => {
  return getChartsData({
    timeFrame,
    stats: companyStats
  }).filter(chartData => visibleChartTypes.includes(chartData.name));
});

/**
 * Returns the fetching properties of the analytics slice.
 */
export const fetchingAnalyticsFlagsSelector = createSelector(state => state.analytics.fetching, fetching => {
  return fetching;
});

/**
 * Returns the selected project chart type
 */
export const selectedProjectChartTimeFrameSelector = createSelector(state => state.analytics.projectOverview.selectedTimeFrame, selectedTimeFrame => {
  return selectedTimeFrame;
});

/**
 * Returns the selected project chart type
 */
export const selectedProjectChartTypeSelector = createSelector(state => state.analytics.projectOverview.selectedChartType, selectedChartType => {
  return selectedChartType;
});

/**
 * Returns the selected chart for the current selected project
 */
export const projectChartSelector = createSelector(selectedProjectIdSelector, state => state.analytics.projectStats, selectedProjectChartTimeFrameSelector, selectedProjectChartTypeSelector, (projectId, projectStats, timeFrame, chartType) => {
  if (!projectId) {
    return undefined;
  }
  const stats = projectStats[projectId] ?? null;
  const charts = getChartsData({
    timeFrame,
    stats
  });
  const selectedChart = charts.find(chart => chart.name === chartType);
  return selectedChart;
});

/**
 * Returns whether the selected project has ever had 360° images, regardless of the selected time frame.
 * Returns undefined while the stats of the project are not fetched yet.
 */
export const hasProjectSpheresSelector = createSelector(selectedProjectIdSelector, state => state.analytics.projectStats, (projectId, projectStats) => {
  const stats = projectId ? projectStats[projectId] : undefined;
  if (!stats) {
    return undefined;
  }
  const {
    cumulative
  } = getBaseStatPairs(stats)[ChartType.projectSpheres];
  return !!cumulative?.data.some(({
    values
  }) => values[0] > 0);
});

/** Returns the in-house analytics dashboard summary that powers the KPI bar. */
export function inHouseAnalyticsDashboardSummarySelector(state) {
  return state.analytics.inHouseDashboard.summary;
}

/** Returns the fetching status of the in-house analytics dashboard summary. */
export function inHouseAnalyticsDashboardFetchingStatusSelector(state) {
  return state.analytics.inHouseDashboard.fetchingStatus;
}